import * as THREE from 'three';
import { Vector3, Quaternion } from '../types';
import { Scene } from '../core/Scene';
import { Transform } from '../core/Transform';

/**
 * BillboardUtils - Shared billboard calculation utilities
 * Ensures consistent billboard behavior across all mesh types
 */
export class BillboardUtils {
    
    /**
     * Calculate billboard rotation to face camera
     * @param transform The transform to calculate billboard rotation for
     * @returns The quaternion rotation to face the camera, or null if no camera available
     */
    public static calculateBillboardRotation(transform: Transform): Quaternion | null {
        const camera = Scene.Instance.getCamera();
        if (!camera) return null;

        const cameraPosition = new Vector3().fromThree(camera.position);
        const objectPosition = transform.getWorldPosition();

        // Calculate direction from object to camera
        const direction = cameraPosition.subtract(objectPosition).normalize();

        // Create proper billboard rotation
        const up = Vector3.up();
        const right = direction.cross(up).normalize();
        const actualUp = right.cross(direction).normalize();

        // Create lookAt matrix
        const lookAtMatrix = new THREE.Matrix4();
        lookAtMatrix.lookAt(objectPosition.toThree(), cameraPosition.toThree(), actualUp.toThree());

        const billboardRotation = new Quaternion().fromThree(new THREE.Quaternion().setFromRotationMatrix(lookAtMatrix));

        // 🎯 ADD: 180-degree rotation around up vector to fix orientation
        const upRotation = new Quaternion().setFromAxisAngle(Vector3.up(), Math.PI);
        const finalRotation = billboardRotation.multiply(upRotation);
        return finalRotation;
    }

    /**
     * Billboard rotation specifically for plane-based labels.
     * Three.js PlaneGeometry has its surface normal on +Z. Using non-camera lookAt
     * semantics (target, eye swapped) orients +Z toward the camera so the front face
     * is visible. The 180-degree Y flip used for shapes makes plane-labels appear edge-on.
     */
    public static calculateLabelBillboardRotation(transform: Transform): Quaternion | null {
        const camera = Scene.Instance.getCamera();
        if (!camera) return null;

        const cameraPosition = new Vector3().fromThree(camera.position);
        const objectPosition = transform.getWorldPosition();

        // Non-camera lookAt: swap eye/target so local +Z points TOWARD camera
        const lookAtMatrix = new THREE.Matrix4();
        lookAtMatrix.lookAt(cameraPosition.toThree(), objectPosition.toThree(), new THREE.Vector3(0, 1, 0));

        return new Quaternion().fromThree(new THREE.Quaternion().setFromRotationMatrix(lookAtMatrix));
    }

    /**
     * Apply plane-label billboard rotation in WORLD space.
     * Labels are often children of POIs/shapes that are rotated by the layouter
     * (e.g. line layouter aligns POI forward to +X). Setting the billboard quaternion
     * as a local rotation would compound with the parent rotation, leaving the label
     * facing the wrong way. setWorldRotation() converts to local-relative-to-parent.
     */
    public static applyLabelBillboardRotation(transform: Transform): boolean {
        const q = BillboardUtils.calculateLabelBillboardRotation(transform);
        if (!q) return false;
        transform.setWorldRotation(q);
        return true;
    }

    /**
     * Calculate Y-only (yaw) billboard rotation: turn to camera around global up axis only.
     */
    public static calculateYawOnlyBillboardRotation(transform: Transform): Quaternion | null {
        const camera = Scene.Instance.getCamera();
        if (!camera) return null;

        const objPos = transform.getWorldPosition().toThree();
        const camPos = camera.position.clone();
        // Project onto XZ plane by aligning Y
        camPos.y = objPos.y;
        const look = new THREE.Matrix4();
        look.lookAt(objPos, camPos, new THREE.Vector3(0,1,0));
        const q = new THREE.Quaternion().setFromRotationMatrix(look);
        return new Quaternion().fromThree(q);
    }

    /** Apply yaw-only billboard rotation. */
    public static applyYawOnlyBillboard(transform: Transform): boolean {
        const q = BillboardUtils.calculateYawOnlyBillboardRotation(transform);
        if (!q) return false;
        // Same world-vs-local fix as applyBillboardRotation.
        transform.setWorldRotation(q);
        return true;
    }

    /**
     * Apply billboard rotation to a transform
     * @param transform The transform to apply billboard rotation to
     * @returns true if billboard was applied, false if no camera available
     */
    public static applyBillboardRotation(transform: Transform): boolean {
        const billboardRotation = BillboardUtils.calculateBillboardRotation(transform);
        if (!billboardRotation) return false;

        // calculateBillboardRotation returns a WORLD-space quaternion (lookAt math
        // uses world-space object & camera positions). setWorldRotation() converts
        // it to the correct local rotation relative to the parent. When the transform
        // has no parent or an identity parent rotation, this matches the old behavior.
        transform.setWorldRotation(billboardRotation);
        return true;
    }
} 